"use client";

import React, { useState } from "react";

type RangeValue = {
  min: number;
  max: number;
};

interface RangeSliderProps {
  min?: number;
  max?: number;
  step?: number;
  initial?: RangeValue;
  onChange?: (range: RangeValue) => void;
  prefix?: string;
  suffix?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 !== 0 ? 1 : 0) + "k";
  }
  return num.toString();
};

export function RangeSlider({
  min = 0,
  max = 100,
  step = 1,
  initial = { min: 20, max: 80 },
  onChange,
  prefix = "",
  suffix = "",
}: RangeSliderProps) {
  const [range, setRange] = useState<RangeValue>(initial);

  const handleChange = (key: keyof RangeValue, value: number) => {
    const newRange = { ...range };

    if (key === "min") {
      newRange.min = Math.min(value, range.max);
    } else {
      newRange.max = Math.max(value, range.min);
    }

    setRange(newRange);
    onChange?.(newRange);
  };

  const percent = (value: number) => ((value - min) / (max - min)) * 100;

  return (
    <div className="w-full">
      {/* Slider container */}
      <div className="relative w-full h-[24px] mb-1">
        {/* Barra base */}
        <div className="absolute top-1/2 left-0 w-full h-[4px] bg-[#C9C8C8] -translate-y-1/2 rounded" />

        {/* Barra seleccionada */}
        <div
          className="absolute h-[4px] bg-[#5B02FF] top-1/2 -translate-y-1/2 z-10 rounded"
          style={{
            left: `${percent(range.min)}%`,
            width: `${percent(range.max) - percent(range.min)}%`,
          }}
        />

        {/* Input para el valor mínimo */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={range.min}
          onChange={(e) => handleChange("min", +e.target.value)}
          className="absolute top-0 left-0 w-full h-[24px] appearance-none bg-transparent pointer-events-none z-20"
        />

        {/* Input para el valor máximo */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={range.max}
          onChange={(e) => handleChange("max", +e.target.value)}
          className="absolute top-0 left-0 w-full h-[24px] appearance-none bg-transparent pointer-events-none z-30"
        />

        {/* Thumbs personalizados */}
        <div
          className="absolute top-1/2 w-[16px] h-[16px] bg-[#5B02FF] rounded-full -translate-y-1/2 -translate-x-1/2 z-20 cursor-pointer"
          style={{ left: `${percent(range.min)}%` }}
          onMouseDown={(e) => {
            const slider = e.currentTarget.parentElement;
            if (!slider) return;

            const startX = e.clientX;
            const startLeft = range.min;
            const sliderWidth = slider.offsetWidth;

            const handleMouseMove = (moveEvent: MouseEvent) => {
              const deltaX = moveEvent.clientX - startX;
              const deltaValue = (deltaX / sliderWidth) * (max - min);
              let newValue = startLeft + deltaValue;
              newValue = Math.max(min, Math.min(newValue, range.max));
              handleChange("min", Math.round(newValue / step) * step);
            };

            const handleMouseUp = () => {
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
          }}
        />

        <div
          className="absolute top-1/2 w-[16px] h-[16px] bg-[#5B02FF] rounded-full -translate-y-1/2 -translate-x-1/2 z-30 cursor-pointer"
          style={{ left: `${percent(range.max)}%` }}
          onMouseDown={(e) => {
            const slider = e.currentTarget.parentElement;
            if (!slider) return;

            const startX = e.clientX;
            const startLeft = range.max;
            const sliderWidth = slider.offsetWidth;

            const handleMouseMove = (moveEvent: MouseEvent) => {
              const deltaX = moveEvent.clientX - startX;
              const deltaValue = (deltaX / sliderWidth) * (max - min);
              let newValue = startLeft + deltaValue;
              newValue = Math.max(range.min, Math.min(newValue, max));
              handleChange("max", Math.round(newValue / step) * step);
            };

            const handleMouseUp = () => {
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
          }}
        />
      </div>

      {/* Valores del rango */}
      <div className="flex justify-between mx-[44px]">
        <span className="text-[14px] font-normal text-[#8A8A8A]">
          {prefix}
          {formatNumber(range.min)}
          {suffix}
        </span>
        <span className="text-[14px] font-normal text-[#8A8A8A]">
          {prefix}
          {formatNumber(range.max)}
          {suffix}
        </span>
      </div>

      <style jsx>{`
        input[type="range"] {
          margin: 0;
          padding: 0;
          pointer-events: none;
        }

        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          background: transparent;
          border-radius: 50%;
          cursor: pointer;
          margin-top: -6px;
          border: none;
          pointer-events: auto;
        }

        input[type="range"]::-moz-range-thumb {
          height: 16px;
          width: 16px;
          background: transparent;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          pointer-events: auto;
        }

        input[type="range"]::-webkit-slider-runnable-track {
          height: 4px;
          background: transparent;
        }

        input[type="range"]::-moz-range-track {
          height: 4px;
          background: transparent;
        }
      `}</style>
    </div>
  );
}
